import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DashboardLayout } from '@/components/DashboardLayout';
import {
  LayoutDashboard, List, AlertTriangle, Scale, Gavel,
  FileText, Layers, Lock, Clock, BookOpen,
  FileSignature, Handshake, CheckSquare, Settings,
  Archive, ArrowLeft, Shield, Brain, BarChart3, Menu, X
} from 'lucide-react';

import DisputeDashboard from '@/components/disputes/DisputeDashboard';
import DisputeRegister from '@/components/disputes/DisputeRegister';
import CreateDisputeWizard from '@/components/disputes/CreateDisputeWizard';
import DisputeWorkspace from '@/components/disputes/DisputeWorkspace';
import PotentialDisputes from '@/components/disputes/PotentialDisputes';
import DisputeNoticesTab from '@/components/disputes/DisputeNoticesTab';
import ContractRulesDispute from '@/components/disputes/ContractRulesDispute';
import DisputeReporting from '@/components/disputes/DisputeReporting';

const NAV_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'register', label: 'Dispute Register', icon: List },
  { id: 'potential', label: 'Potential Disputes', icon: AlertTriangle },
  { id: 'formal', label: 'Formal Disputes', icon: Scale },
  { id: 'notices', label: 'Notices', icon: FileText },
  { id: 'reporting', label: 'Reporting', icon: BarChart3 },
  { id: 'rules', label: 'Templates & Rules', icon: Settings },
  { id: 'archive', label: 'Archive', icon: Archive, coming: true },
];

export default function DisputeEngine() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'dashboard';
  const matterId = searchParams.get('matter');

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.disputes.getSettings(projectId);
        setSettings(res.data);
      } catch { /* silent */ } finally { setLoading(false); }
    })();
  }, [projectId]);

  const setTab = (t) => {
    const params = new URLSearchParams();
    params.set('tab', t);
    setSearchParams(params);
  };

  const openMatter = (id) => {
    const params = new URLSearchParams();
    params.set('tab', 'workspace');
    params.set('matter', id);
    setSearchParams(params);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin h-8 w-8 border-2 border-slate-300 border-t-slate-800 rounded-full" />
      </div>
    );
  }

  const renderContent = () => {
    if (showWizard) {
      return <CreateDisputeWizard projectId={projectId} settings={settings} onClose={() => setShowWizard(false)} onCreated={(id) => { setShowWizard(false); openMatter(id); }} />;
    }
    if (matterId && tab === 'workspace') {
      return <DisputeWorkspace projectId={projectId} matterId={matterId} settings={settings} onBack={() => setTab('register')} />;
    }
    switch (tab) {
      case 'dashboard':
        return <DisputeDashboard projectId={projectId} settings={settings} onNavigate={setTab} onOpenMatter={openMatter} />;
      case 'register':
        return <DisputeRegister projectId={projectId} settings={settings} onOpenMatter={openMatter} onCreateNew={() => setShowWizard(true)} />;
      case 'potential':
        return <PotentialDisputes projectId={projectId} settings={settings} onOpenMatter={openMatter} onCreateNew={() => setShowWizard(true)} statusFilter="potential_dispute" />;
      case 'formal':
        return <PotentialDisputes projectId={projectId} settings={settings} onOpenMatter={openMatter} onCreateNew={() => setShowWizard(true)} statusFilter="formal" />;
      case 'notices':
        return <DisputeNoticesTab projectId={projectId} settings={settings} />;
      case 'reporting':
        return <DisputeReporting projectId={projectId} />;
      case 'rules':
        return <ContractRulesDispute projectId={projectId} settings={settings} />;
      default:
        return (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <Shield className="h-16 w-16 text-slate-200 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-slate-400">Coming Soon</h3>
              <p className="text-sm text-slate-400 mt-1">This section is under development</p>
            </div>
          </div>
        );
    }
  };

  const profile = settings?.profile || {};
  const family = settings?.contract_family || 'NEC';

  return (
    <DashboardLayout>
    <div className="flex flex-col md:flex-row h-[calc(100vh-64px)]" data-testid="dispute-engine">
      {/* Mobile Header Bar */}
      <div className="md:hidden bg-white border-b border-slate-200 px-3 py-2.5 flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/projects/${projectId}`)} className="gap-1 text-xs text-slate-500 p-1">
          <ArrowLeft className="h-3.5 w-3.5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xs font-barlow font-bold uppercase tracking-wider text-slate-800 truncate">Dispute Resolution</h1>
        </div>
        <Badge variant="outline" className="text-[10px] flex-shrink-0">{family}</Badge>
        <Button variant="ghost" size="sm" className="p-1" onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)} data-testid="mobile-sidebar-toggle">
          {mobileSidebarOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
        </Button>
      </div>

      {/* Mobile Nav Tabs (horizontal scroll) */}
      <div className="md:hidden bg-white border-b border-slate-200 overflow-x-auto">
        <div className="flex gap-1 px-2 py-1.5 min-w-max">
          {NAV_ITEMS.filter(i => !i.coming).map(item => {
            const Icon = item.icon;
            const isActive = tab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setTab(item.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                  isActive ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
                }`}
                data-testid={`mobile-nav-${item.id}`}
              >
                <Icon className="h-3.5 w-3.5" />
                {item.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Mobile New Dispute Button */}
      <div className="md:hidden px-3 py-2 bg-white border-b border-slate-200">
        <Button size="sm" className="w-full bg-slate-900 hover:bg-slate-800 gap-1 text-xs" onClick={() => setShowWizard(true)} data-testid="mobile-create-dispute-btn">
          <Scale className="h-3.5 w-3.5" /> New Dispute
        </Button>
      </div>

      {/* Desktop Left Nav */}
      <aside className="hidden md:flex w-56 border-r border-slate-200 bg-white flex-col overflow-y-auto flex-shrink-0">
        <div className="p-4 border-b border-slate-100">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/projects/${projectId}`)} className="gap-1 text-xs text-slate-500 -ml-2 mb-2">
            <ArrowLeft className="h-3.5 w-3.5" /> Project
          </Button>
          <h1 className="text-sm font-barlow font-bold uppercase tracking-wider text-slate-800">Dispute Resolution</h1>
          <Badge variant="outline" className="text-[10px] mt-1">{family}</Badge>
        </div>
        <nav className="flex-1 p-2 space-y-0.5">
          {NAV_ITEMS.map(item => {
            const Icon = item.icon;
            const isActive = tab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => !item.coming && setTab(item.id)}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                  isActive ? 'bg-slate-900 text-white' : item.coming ? 'text-slate-300 cursor-default' : 'text-slate-600 hover:bg-slate-100'
                }`}
                data-testid={`nav-${item.id}`}
              >
                <Icon className="h-4 w-4 flex-shrink-0" />
                <span className="truncate">{item.label}</span>
                {item.coming && <span className="ml-auto text-[8px] text-slate-300 uppercase">Soon</span>}
              </button>
            );
          })}
        </nav>
        <div className="p-3 border-t border-slate-100">
          <Button size="sm" className="w-full bg-slate-900 hover:bg-slate-800 gap-1 text-xs" onClick={() => setShowWizard(true)} data-testid="create-dispute-btn">
            <Scale className="h-3.5 w-3.5" /> New Dispute
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto bg-slate-50 p-3 sm:p-4 md:p-6">
        {renderContent()}
      </main>
    </div>
    </DashboardLayout>
  );
}
